import { HTMLAttributes, memo, useState } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useLanguage } from '../../hooks';
import { LangTree } from '../../types';
import { CustomTimePicker } from './customTimePicker';

// Shared visual style for the selects (neutral secondary.light border + 8px
// radius, 16px CaretDown icon in secondary.dark). It only defines the normal
// state: the system hover/focus/error stay intact through MUI's specificity.
const dropdownVisual = {
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'secondary.light',
    borderRadius: '8px',
  },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'secondary.light',
  },
  // The error border keeps 1px (same as the normal one) even when the field is
  // focused, so dropdowns in error render at the same thickness.
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px',
  },
  '& .MuiSelect-icon': {
    color: 'secondary.dark',
    fontSize: '20px',
  },
  '&.Mui-disabled .MuiSelect-icon': {
    color: 'grey.400',
  },
  '&.Mui-disabled .MuiSelect-select': {
    WebkitTextFillColor: '#B8BABF',
  },
};

const customStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    // Fixed width matching the duration block (two 96px dropdowns + gaps + the
    // "Horas"/"Minutos" units), so start time, duration and interval share one
    // grid and align on their right edge.
    width: '311px',
    maxWidth: '100%',
    margin: 'auto',
  },
  formControlText: {
    marginTop: '12px',
    marginLeft: '0px',
  },
  // The interval header sits 16px below the end-of-workday helper.
  intervalHeaderText: {
    marginTop: '16px',
    marginLeft: '0px',
  },
  // Duration header to dropdown row: 2px (the row's marginTop).
  durationRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    marginTop: '2px',
  },
  // Each dropdown with its unit to the right, vertically centred and 8px apart,
  // so the pair reads as a single control.
  durationGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  durationSelect: {
    height: '34px',
    width: '96px',
    ...dropdownVisual,
  },
  unitText: {
    fontSize: '12px',
    fontWeight: 400,
    color: (theme) => theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  select: {
    height: '34px',
    ...dropdownVisual,
  },
  menuItem: { fontSize: '14px' },
  // Max height of the duration menu = 12 visible rows (34px each + 16px list
  // padding), the same count as the minutes dropdown. Hours and minutes menus
  // then share one height; the hours menu scrolls for the rest.
  durationMenuPaper: { maxHeight: '424px' },
  // Dropdowns to end-time helper: 4px.
  endTimeHelper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  endTimeText: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#6E7178',
  },
  endTimeIcon: {
    fontSize: '16px',
    color: (theme) => theme.palette.primary.main,
  },
  durationError: {
    marginLeft: 0,
    marginTop: '4px',
    color: (theme) => theme.palette.error.main,
  },
  placeholderText: {
    color: (theme) => theme.palette.text.secondary,
  },
  intervalHelperText: {
    marginLeft: 0,
    marginTop: '4px',
    fontSize: '12px',
    color: '#6E7178',
  },
  startTimePicker: {
    borderRadius: '8px',
  },
  startTimePickerClosed: {
    borderColor: 'secondary.light',
  },
  startTimePickerIcon: {
    fontSize: '20px',
    color: 'secondary.dark',
  },
} satisfies Record<string, SxProps<Theme>>;

export const getTimeIntervalAsLabel = (lang: LangTree, minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const minutesRest = minutes % 60;
  let label = '';
  if (hours > 0) {
    label += `${hours} ${hours > 1 ? lang.form_builder.hoursLabel : lang.form_builder.hourLabel}`;
    if (minutesRest > 0)
      label += ` ${lang.form_builder.andMinutesLabel} ${minutesRest} ${lang.form_builder.minutesLabel}`;
  } else {
    label += `${minutesRest} ${lang.form_builder.minutesLabel}`;
  }
  return label;
};

// Interval label with full words ("Cada 1 hora", "Cada 2 horas", "Cada 1 hora y
// 30 minutos"). Built here instead of reusing getTimeIntervalAsLabel (which uses
// the "hr"/"hrs" abbreviations) so the answer view keeps rendering intervals
// exactly as it does today.
const getIntervalDropdownLabel = (lang: LangTree, minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const minutesRest = minutes % 60;
  let label = '';
  if (hours > 0) {
    label += `${hours} ${hours > 1 ? lang.form_builder.hoursWord : lang.form_builder.hourWord}`;
    if (minutesRest > 0)
      label += ` ${lang.form_builder.andMinutesLabel} ${minutesRest} ${lang.form_builder.minutesLabel}`;
  } else {
    label += `${minutesRest} ${lang.form_builder.minutesLabel}`;
  }
  return `${lang.form_builder.every} ${label}`;
};

// Converts "HH:MM" to minutes since midnight.
const hhmmToMinutes = (value: string): number => {
  const [hours, minutes] = (value || '00:00').split(':').map((part) => parseInt(part, 10) || 0);
  return hours * 60 + minutes;
};

// Converts minutes (0..1439) to "HH:MM".
const minutesToHHMM = (minutes: number): string => {
  const safe = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${hours.toString().padStart(2, '0')}:${rest.toString().padStart(2, '0')}`;
};

const MINUTES_IN_DAY = 24 * 60;
const MIN_DURATION_MINUTES = 5;
const MAX_DURATION_HOURS = 24;

// Splits workingTime (minutes as a string) into hours and minutes for the
// dropdowns. Empty/undefined => both empty (unconfigured). '0' => 0 h 00 min.
const splitWorkingTime = (value: string): { hours: number | ''; minutes: number | '' } => {
  if (!value) return { hours: '', minutes: '' };
  const total = parseInt(value, 10);
  if (Number.isNaN(total)) return { hours: '', minutes: '' };
  return { hours: Math.floor(total / 60), minutes: total % 60 };
};

// Options for the duration wheels.
const HOURS_OPTIONS = Array.from({ length: 25 }, (_, index) => index); // 0..24
const MINUTES_OPTIONS = Array.from({ length: 12 }, (_, index) => index * 5); // 0, 5, ... 55

// Base interval catalogue (in minutes). Only the ones that divide the configured
// workday exactly are shown when the list opens.
const intervalDefaultOptions = ['5', '10', '15', '20', '30', '45', '60', '90', '120'];

// SelectDisplayProps is typed as HTMLAttributes, which does not accept data-*
// attributes, so the test id needs the cast. Done once here instead of repeating
// it on every Select.
const displayTestId = (id: string) => ({ 'data-testid': id }) as HTMLAttributes<HTMLDivElement>;

interface ActivityPeriodTimeSettingProps {
  questionId: string;
  startTime: string;
  onActivityPeriodTimeSettingChange: (
    id: string,
    value: string,
    action: string,
    tableId?: string,
  ) => void;
  tableId?: string;
  workingTime: string;
  intervalDefault: string;
  /** The form turns this on after a failed save attempt. Before that an
   *  unconfigured question is not marked red: it starts that way on purpose. */
  showValidationErrors?: boolean;
}

/**
 * Workday duration and interval configuration for a data-list activity question.
 *
 * Layout notes that the markup cannot state on its own:
 * - Both section headers live outside the FormControl that carries the error, so
 *   they never inherit the red colour and keep the styling of every other label.
 * - Hours and minutes each get their own FormControl, so focus is not shared
 *   (focusing Hours must not tint Minutes blue); the error is kept per select
 *   through `error={durationInvalid}`.
 * - The interval helper text stays visible in both field states, enabled and
 *   disabled, because it is what explains why the list changes with the
 *   duration. It gives its place to the error while one exists, so two messages
 *   are never stacked.
 */
const ActivityPeriodTimeSetting = memo((props: ActivityPeriodTimeSettingProps) => {
  const {
    questionId,
    startTime,
    onActivityPeriodTimeSettingChange,
    tableId,
    workingTime,
    intervalDefault,
    showValidationErrors = false,
  } = props;
  const lang = useLanguage();

  // Local dropdown state. '' means "nothing selected" (unconfigured workday);
  // a number means an explicit selection (including 0).
  const initialDuration = splitWorkingTime(workingTime);
  const [hours, setHours] = useState<number | ''>(initialDuration.hours);
  const [minutes, setMinutes] = useState<number | ''>(initialDuration.minutes);

  const isConfigured = hours !== '' && minutes !== '';
  const totalMinutes = isConfigured ? (hours as number) * 60 + (minutes as number) : NaN;
  const durationValid = isConfigured && totalMinutes >= MIN_DURATION_MINUTES;
  // Picking 0 h 00 min is an error visible at once: the user already expressed a
  // duration and it is invalid. An untouched duration is only marked after a
  // failed save, which is when it stops being work in progress.
  const durationInvalid = (isConfigured || showValidationErrors) && !durationValid;

  // An interval applies when it divides a valid workday exactly.
  const intervalDivides = (intervalMinutes: number): boolean =>
    durationValid && totalMinutes % intervalMinutes === 0;

  const availableIntervals = intervalDefaultOptions
    .map((option) => parseInt(option, 10))
    .filter((intervalMinutes) => intervalDivides(intervalMinutes));

  const currentIntervalMinutes = intervalDefault ? parseInt(intervalDefault, 10) : NaN;
  // Only an interval that is still compatible shows as selected.
  const selectedInterval = availableIntervals.includes(currentIntervalMinutes)
    ? currentIntervalMinutes
    : '';
  // The interval is enabled only with a valid duration; the placeholder guides
  // the step-by-step configuration.
  const intervalPlaceholder = durationValid
    ? lang.form_builder.activityIntervalPlaceholderEmpty
    : lang.form_builder.activityIntervalPlaceholderDisabled;
  // The interval is only demanded once a valid duration exists: without one the
  // field is disabled and the duration is the problem to solve first.
  const intervalInvalid = showValidationErrors && durationValid && selectedInterval === '';

  // End of the workday (only relevant with a valid duration).
  const startMinutes = hhmmToMinutes(startTime);
  const endTotalMinutes = startMinutes + (Number.isNaN(totalMinutes) ? 0 : totalMinutes);
  const endsNextDay = endTotalMinutes >= MINUTES_IN_DAY;
  const endTimeLabel = minutesToHHMM(((endTotalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY);
  const endTimeMessage = (endsNextDay
    ? lang.form_builder.workdayEndsAtNextDay
    : lang.form_builder.workdayEndsAt
  ).replace('{time}', endTimeLabel);

  // Persists the duration (minutes as a string) when both dropdowns have a
  // value; if either is missing it stores empty (unconfigured). If the chosen
  // interval stops dividing the new duration it is cleared, never reassigned.
  const commitDuration = (nextHours: number | '', nextMinutes: number | '') => {
    setHours(nextHours);
    setMinutes(nextMinutes);
    const configured = nextHours !== '' && nextMinutes !== '';
    const total = configured ? (nextHours as number) * 60 + (nextMinutes as number) : null;
    onActivityPeriodTimeSettingChange(
      questionId,
      configured ? String(total) : '',
      'workingTime',
      tableId,
    );
    if (!Number.isNaN(currentIntervalMinutes) && intervalDefault) {
      const stillDivides = total !== null && total > 0 && total % currentIntervalMinutes === 0;
      if (!stillDivides) {
        onActivityPeriodTimeSettingChange(questionId, '', 'intervalDefault', tableId);
      }
    }
  };

  // Workday ceiling: exactly 24 h. At 24 h the minutes are pinned to 00 (24 h 05
  // min and above are not allowed); between 0 and 23 h every option is offered.
  const isMaxHours = hours === MAX_DURATION_HOURS;
  const minuteOptions = isMaxHours ? [0] : MINUTES_OPTIONS;

  // The counterpart that was never touched counts as 0, so picking only hours
  // commits "N h 00 min" instead of nothing. Without this a half-made pick is
  // stored as an empty workingTime and is lost when the question collapses.
  const handleHoursChange = (nextHours: number) => {
    const currentMinutes = minutes === '' ? 0 : minutes;
    const nextMinutes = nextHours === MAX_DURATION_HOURS ? 0 : currentMinutes;
    commitDuration(nextHours, nextMinutes);
  };
  const handleMinutesChange = (nextMinutes: number) => {
    const safeMinutes = isMaxHours ? 0 : nextMinutes;
    commitDuration(hours === '' ? 0 : hours, safeMinutes);
  };

  const handleIntervalChange = (value: number) => {
    if (intervalDivides(value)) {
      onActivityPeriodTimeSettingChange(questionId, value.toString(), 'intervalDefault', tableId);
    }
  };

  return (
    <Box sx={customStyles.root}>
      <FormControl>
        <FormHelperText sx={customStyles.formControlText}>
          {lang.form_builder.activityStartTime}
        </FormHelperText>
        <CustomTimePicker
          value={startTime}
          onChange={(value) =>
            onActivityPeriodTimeSettingChange(questionId, value, 'startTime', tableId)
          }
          sx={customStyles.startTimePicker}
          closedSx={customStyles.startTimePickerClosed}
          IconComponent={KeyboardArrowDownRounded}
          iconSx={customStyles.startTimePickerIcon}
        />
      </FormControl>

      <FormHelperText
        sx={customStyles.formControlText}
        data-testid='activity-duration-header'
      >
        {lang.form_builder.activityDuration}
      </FormHelperText>
      <Box sx={customStyles.durationRow}>
        <Box sx={customStyles.durationGroup}>
          <FormControl error={durationInvalid}>
            <Select
              displayEmpty
              value={hours}
              sx={customStyles.durationSelect}
              error={durationInvalid}
              IconComponent={KeyboardArrowDownRounded}
              MenuProps={{ PaperProps: { sx: customStyles.durationMenuPaper } }}
              renderValue={(value) => String(value)}
              onChange={(e) => handleHoursChange(Number(e.target.value))}
              SelectDisplayProps={displayTestId('activity-duration-hours')}
            >
              {HOURS_OPTIONS.map((hour) => (
                <MenuItem
                  key={hour}
                  value={hour}
                  sx={customStyles.menuItem}
                  data-testid={`activity-duration-hours-option-${hour}`}
                >
                  {hour}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={customStyles.unitText}>
            {lang.form_builder.activityDurationHours}
          </Typography>
        </Box>

        <Box sx={customStyles.durationGroup}>
          <FormControl error={durationInvalid}>
            <Select
              displayEmpty
              value={minutes}
              sx={customStyles.durationSelect}
              error={durationInvalid}
              IconComponent={KeyboardArrowDownRounded}
              MenuProps={{ PaperProps: { sx: customStyles.durationMenuPaper } }}
              renderValue={(value) => (String(value) === '' ? '' : String(value).padStart(2, '0'))}
              onChange={(e) => handleMinutesChange(Number(e.target.value))}
              SelectDisplayProps={displayTestId('activity-duration-minutes')}
            >
              {minuteOptions.map((minute) => (
                <MenuItem
                  key={minute}
                  value={minute}
                  sx={customStyles.menuItem}
                  data-testid={`activity-duration-minutes-option-${minute}`}
                >
                  {minute.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography sx={customStyles.unitText}>
            {lang.form_builder.activityDurationMinutes}
          </Typography>
        </Box>
      </Box>
      {durationInvalid && (
        <FormHelperText
          sx={customStyles.durationError}
          data-testid='activity-duration-error'
        >
          {lang.form_builder.activityDurationInvalid}
        </FormHelperText>
      )}
      {durationValid && (
        <Box
          sx={customStyles.endTimeHelper}
          data-testid='activity-end-time-helper'
        >
          <AccessTimeIcon sx={customStyles.endTimeIcon} />
          <Typography sx={customStyles.endTimeText}>{endTimeMessage}</Typography>
        </Box>
      )}

      <FormHelperText
        sx={customStyles.intervalHeaderText}
        data-testid='activity-interval-header'
      >
        {lang.form_builder.activityInterval}
      </FormHelperText>
      <FormControl error={intervalInvalid}>
        <Select
          displayEmpty
          disabled={!durationValid}
          error={intervalInvalid}
          value={selectedInterval}
          sx={customStyles.select}
          IconComponent={KeyboardArrowDownRounded}
          renderValue={(value) =>
            value ? (
              getIntervalDropdownLabel(lang, Number(value))
            ) : (
              <Box component='span' sx={customStyles.placeholderText}>
                {intervalPlaceholder}
              </Box>
            )
          }
          onChange={(e) => handleIntervalChange(Number(e.target.value))}
          SelectDisplayProps={displayTestId('activity-interval')}
        >
          {availableIntervals.map((intervalMinutes) => (
            <MenuItem
              key={intervalMinutes}
              value={intervalMinutes}
              sx={customStyles.menuItem}
              data-testid={`activity-interval-option-${intervalMinutes}`}
            >
              {getIntervalDropdownLabel(lang, intervalMinutes)}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText
          sx={intervalInvalid ? customStyles.durationError : customStyles.intervalHelperText}
          data-testid='activity-interval-helper'
        >
          {intervalInvalid
            ? lang.form_builder.activityIntervalRequired
            : lang.form_builder.activityIntervalHelperText}
        </FormHelperText>
      </FormControl>
    </Box>
  );
});

ActivityPeriodTimeSetting.displayName = 'ActivityPeriodTimeSetting';
export { ActivityPeriodTimeSetting as ActivityPeriodTimeSetting };
